import { useEffect } from 'react';

import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import DeliverySignOutContainer from '../FrontDesk/LookUp/DeliverySignOutContainer';
import AsyncTableComponent from '../Table/AsyncTableContainer';
import CreateDeliveryContainer from './CreateDeliveryContainer';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';
import ModalHandlerComponent from '../Modal/ModalHandlerComponent';
import CheckmarkOrX from '../Table/CheckmarkOrXComponent';

import {
  filterObjectByKeyValue,
  formatDate,
  isAdmin,
  isStaffRole,
  toSentenceCase,
} from '../../utils';
import { contactPersonRole } from '../Person/utils';
import { buildingsForFilterTypes } from '../Shared/utils/types';
import { getAddedByCell } from '../Table/utils';
import { getExportTitle } from '../ViewExport/utils';
import { getDeliveryTypeIcon } from './utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../Table/constants';
import { ADD_DELIVERY, DELIVERY_SIGN_OUT } from './constants';

import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../staticData';
import WithLink from '../Linkify';
import { CUSTOM } from '../Modal/modalTypes';
import StyledLink from '../StyledLink';
import CellPersonWithImage from '../Table/CellPersonWithImage';
import ListActions from '../Table/ListActions';
import {
  buildingFilter,
  limitFilter,
  openDeliveryFilter,
  unitFilter,
} from '../Table/filters';
import Tooltip from '../Tooltip';
import { deliveryActions } from './DeliveryAction';
import DeliveryListExport from './DeliveryListExport';

const DeliveryListComponent = (props) => {
  const { t } = useTransl();

  const {
    deliveryTypes,
    onLoad,
    building,
    buildings,
    items,
    getItems,
    listParams,
    listKey,
    user,
    pmc,
    modalData,
    setModalData,
    getUnits,
    units,
    onExport,
    onGetChartData,
    isFrontDesk: isFrontDeskPage,
    onRefreshList,
  } = props;

  const isFrontDesk = Boolean(building?.buildingData?.frontDeskEnabledFlag);

  const columns = [
    {
      Header: t('title.building_s'),
      id: 'building',
      headerClassName: 'sort',
      show: !building?.id,
      accessor: ({ building, buildingName }) => {
        if (!building) return buildingName;
        return (
          <StyledLink to={`/building/${building}`}>{buildingName}</StyledLink>
        );
      },
    },
    {
      Header: t('title.unit'),
      id: 'unit',
      sortable: false,
      width: 80,
      accessor: ({ building, general, unit, unitName }) => {
        if (!building) return '';
        if (general) return t('delivery.generalDelivery');
        if (unit) {
          return (
            <StyledLink to={`/building/${building}/units/${unit}`}>
              {unitName}
            </StyledLink>
          );
        }
        return null;
      },
    },
    {
      Header: t('title.recipient'),
      id: 'recipient_first_name',
      sortable: true,
      headerClassName: 'sort',
      accessor: (entry) => {
        if (get(entry, 'general', null)) return '';
        const person = get(entry, 'recipient', null);
        const unit = get(entry, 'unit', null);
        const buildingId = get(entry, 'building', null);
        const personId = get(entry, 'recipient.id', null);
        const roleId = get(entry, 'recipient.role', null);
        const personType = isStaffRole(roleId) ? 'staff' : 'residents';

        const linkUrl = `/building/${buildingId}/${personType}/${personId}`;
        const isDetailsAvailable = get(
          entry,
          'recipient.detailsAvailable',
          false,
        );

        return isEmpty(person) && unit ? (
          t('delivery.unitDelivery')
        ) : (
          <CellPersonWithImage
            person={person}
            linkUrl={isDetailsAvailable ? linkUrl : null}
          />
        );
      },
    },
    {
      Header: t('title.recipientRole'),
      id: 'recipient_role',
      sortable: false,
      accessor: ({ recipient = {} }) => contactPersonRole(recipient),
    },
    {
      Header: t('title.createdAt'),
      id: 'created_at',
      headerClassName: 'sort',
      accessor: ({ createdAt }) => formatDate(createdAt),
    },
    {
      Header: t('title.signedOutAt'),
      id: 'signed_out_at',
      headerClassName: 'sort',
      accessor: ({ signOutData }) => formatDate(signOutData?.signedOutAt),
      show: isFrontDesk,
    },
    {
      Header: t('title.deliveryType'),
      id: 'type',
      headerClassName: 'sort',
      accessor: ({ type }) => {
        const icon = getDeliveryTypeIcon(deliveryTypes, type?.id);
        return (
          <Box display="flex" alignItems="center">
            {icon && (
              <Box mr="1rem">
                <ExpandableImageComponent
                  src={icon}
                  caption={type?.displayName}
                  height="3.2rem"
                  isSquare
                />
              </Box>
            )}
            {type?.displayName}
          </Box>
        );
      },
    },
    {
      Header: isFrontDesk ? t('title.outgoingItem') : t('title.isPickUp'),
      id: 'pick_up',
      headerClassName: 'sort',
      width: 100,
      accessor: ({ pickUp }) => <CheckmarkOrX value={!!pickUp} />,
    },
    {
      Header: t('title.signOutRequired'),
      id: 'delivery_sign_out_required',
      headerClassName: 'sort',
      width: 100,
      show: isFrontDesk,
      accessor: ({ signOutData }) => <CheckmarkOrX value={!!signOutData?.id} />,
    },
    {
      Header: t('title.needsSignature'),
      id: 'signature_required',
      headerClassName: 'sort',
      width: 100,
      show: !isFrontDesk,
      accessor: ({ signatureRequired }) => (
        <CheckmarkOrX value={!!signatureRequired} />
      ),
    },
    {
      Header: t('title.comments'),
      id: 'comments',
      sortable: false,
      width: 200,
      accessor: ({ comments, snapshots }) => (
        <Box display="flex" alignItems="center">
          {snapshots?.length > 0 && (
            <Box mr="1rem">
              <ExpandableImageComponent
                src={snapshots[0]?.url}
                caption={t('title.cameraSnapshot')}
                height="3.2rem"
              />
            </Box>
          )}
          <Tooltip tooltip={comments}>
            <WithLink>{comments}</WithLink>
          </Tooltip>
        </Box>
      ),
    },
    {
      Header: t('title.addedBy'),
      id: 'creator_first_name',
      headerClassName: 'sort',
      accessor: (entry) => {
        const { isService } = entry?.creator || {};
        if (isService && !isAdmin()) {
          return (
            <CellPersonWithImage
              person={{ firstName: t('roles.carsonService'), lastName: '' }}
            />
          );
        }
        return getAddedByCell(entry, user);
      },
    },
    {
      Header: t('title.role'),
      id: 'role',
      sortable: false,
      accessor: ({ creator = {} }) => contactPersonRole(creator),
    },
    {
      Header: t('title.signedOutBy'),
      id: 'signed_out_by',
      headerClassName: 'sort',
      show: isFrontDesk,
      accessor: ({ signOutData }) => {
        if (!signOutData?.signee) return null;
        return <CellPersonWithImage person={signOutData?.signee} />;
      },
    },
    {
      Header: t('title.signedOutType'),
      id: 'signed_out_type',
      sortable: false,
      show: isFrontDesk,
      accessor: ({ signOutData }) => {
        if (!signOutData?.signOutType) return null;
        return toSentenceCase(signOutData.signOutType);
      },
    },
  ];

  if (isFrontDesk) {
    columns.push(deliveryActions(setModalData));
  }

  const buildingId = building && building.id ? building.id : null;
  const filteredBuildings =
    pmc && pmc.id
      ? filterObjectByKeyValue(buildings, 'parent.id', pmc.id)
      : buildings;

  const filters = [
    buildingFilter(listParams, filteredBuildings),
    limitFilter(listParams),
    openDeliveryFilter(listParams),
  ];

  const selectedBuildingId = listParams?.building;

  useEffect(() => {
    selectedBuildingId && getUnits(selectedBuildingId);
  }, [selectedBuildingId]);

  if (selectedBuildingId) {
    filters.splice(1, 0, unitFilter(listParams, units));
  }

  if (buildingId) {
    filters.shift();
  }

  const addDeliveryModalProps = {
    modalName: ADD_DELIVERY,
    modal: {
      modalType: CUSTOM,
      modalProps: {
        title: t('delivery.add'),
      },
    },
    onModalClose: () => setModalData({}),
  };

  const addDelivery = isFrontDeskPage
    ? []
    : [
        {
          label: t('delivery.add'),
          onClick: () => setModalData(addDeliveryModalProps),
        },
      ];

  const defaultParams = {
    ordering: '-created_at',
    building: buildingId,
    pmc: pmc && pmc.id ? pmc.id : null,
  };
  const tableDataType = TABLE_DATA_TYPES.deliveries;

  const title = t('title.deliveries');
  const exportTitle = getExportTitle(title, pmc, building);
  const exportActions = isFrontDeskPage ? null : (
    <DeliveryListExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  const chartMetadata = isFrontDeskPage
    ? null
    : {
        chartId: camelCase(title),
        title: t('title.lastDeliveries'),
        label: t('title.delivery'),
        onGetChartData,
        params: {
          building: building?.id,
          pmc: pmc?.id,
        },
      };

  return (
    <>
      <AsyncTableComponent
        tableId={TABLE_ID.deliveriesTable}
        title={isFrontDeskPage ? t('title.frontDesk') : t('title.deliveries')}
        columns={columns}
        action={<ListActions actions={addDelivery} />}
        defaultSort={TABLE_CONSTANTS.created_at}
        defaultSortDesc
        manual
        filters={filters}
        showFiltersAreApplied={false}
        items={items}
        onLoad={onLoad}
        getItems={getItems}
        listParams={listParams}
        defaultParams={defaultParams}
        tableDataType={tableDataType}
        listKey={listKey}
        dateRangeType={DATE_RANGES_TYPES.created_at}
        datePicker
        exportActions={exportActions}
        chartMetadata={chartMetadata}
        searchAutoFocus={!isFrontDeskPage}
        hasToggleFilters={!isFrontDeskPage}
      />
      {modalData.modalName === ADD_DELIVERY && (
        <ModalHandlerComponent {...modalData}>
          <CreateDeliveryContainer
            onModalClose={() => {
              onRefreshList();
              setModalData({});
            }}
          />
        </ModalHandlerComponent>
      )}
      {modalData.modalName === DELIVERY_SIGN_OUT && (
        <ModalHandlerComponent {...modalData}>
          <DeliverySignOutContainer
            unit={modalData.unit}
            onModalClose={() => {
              !isFrontDeskPage && onRefreshList();
              setModalData({});
            }}
          />
        </ModalHandlerComponent>
      )}
    </>
  );
};

DeliveryListComponent.defaultProps = {
  deliveryTypes: [],
  building: null,
  pmc: null,
  isFrontDesk: false,
  onRefreshList: () => {},
};

DeliveryListComponent.propTypes = {
  building: PropTypes.instanceOf(Object),
  pmc: PropTypes.instanceOf(Object),
  user: PropTypes.instanceOf(Object).isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  buildings: buildingsForFilterTypes.isRequired,
  units: PropTypes.instanceOf(Object).isRequired,
  deliveryTypes: PropTypes.instanceOf(Array),
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  getUnits: PropTypes.func.isRequired,
  modalData: PropTypes.instanceOf(Object).isRequired,
  setModalData: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onGetChartData: PropTypes.func.isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
  isFrontDesk: PropTypes.bool,
  onRefreshList: PropTypes.func,
};

export default DeliveryListComponent;
